import {
  cloneElement,
  isValidElement,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
  useEffect,
  useState,
} from "react";
import { cn } from "@/shared/lib/cn";
import { Popover } from "./Popover";
import { CaretRightIcon, CheckIcon } from "./icons";

/** One row in a {@link Menu}. */
export interface MenuItem {
  id: string;
  label: string;
  icon?: ReactNode;
  description?: string;
  shortcut?: string;
  group?: string;
  /** When defined, this row is a radio choice with an explicit selected state. */
  checked?: boolean;
  onSelect?: () => void;
  children?: MenuItem[];
  /** Visually separated by extra spacing above (e.g. destructive actions). */
  separated?: boolean;
  disabled?: boolean;
  tone?: "default" | "destructive";
}

export interface MenuProps {
  trigger: (args: { open: boolean; toggle: () => void }) => ReactNode;
  items: MenuItem[];
  /** Optional non-interactive context shown before the action rows. */
  header?: ReactNode;
  side?: "top" | "bottom";
  align?: "start" | "end";
  label: string;
  panelClassName?: string;
}

const menuPanelClass = "w-[min(280px,calc(100vw-24px))] rounded-[12px] p-1.5";

/** Actions and nested side menus share the same keyboard and overlay behavior. */
export function Menu({ trigger, items, header, side, align = "end", label, panelClassName }: MenuProps) {
  return (
    <Popover
      label={label}
      panelRole={null}
      openOnArrowKeys
      side={side}
      align={align}
      panelClassName={cn(menuPanelClass, panelClassName)}
      trigger={({ open, toggle, panelId }) => {
        const triggerNode = trigger({ open, toggle });
        return isValidElement(triggerNode)
          ? cloneElement(triggerNode as ReactElement<MenuTriggerProps>, {
              "aria-controls": panelId,
              "aria-expanded": open,
              "aria-haspopup": "menu",
            })
          : triggerNode;
      }}
    >
      {({ close, panelId }) => <MenuPanel items={items} header={header} label={label} panelId={panelId} closeRoot={close} />}
    </Popover>
  );
}

function MenuPanel({ items, header, label, panelId, closeRoot, closeBranch }: {
  items: MenuItem[];
  header?: ReactNode;
  label: string;
  panelId: string;
  closeRoot: () => void;
  closeBranch?: () => void;
}) {
  const firstEnabledId = items.find(item => !item.disabled)?.id ?? null;
  const itemStateKey = JSON.stringify(items.map(item => [item.id, !!item.disabled]));
  const [activeItemId, setActiveItemId] = useState(firstEnabledId);
  const [submenuID, setSubmenuID] = useState<string | null>(null);
  const [focusSubmenu, setFocusSubmenu] = useState(false);
  useEffect(() => {
    setActiveItemId(firstEnabledId);
    setSubmenuID(null);
  }, [firstEnabledId, itemStateKey]);

  const openSubmenu = (item: MenuItem, focus: boolean) => {
    if (item.disabled || !item.children?.length) return;
    setFocusSubmenu(focus);
    setSubmenuID(item.id);
  };

  return <div
    id={panelId}
    role="menu"
    aria-label={label}
    className="flex min-h-0 flex-col overflow-y-auto"
    onKeyDown={event => {
      // Portaled descendants still bubble through React; each menu owns only its rows.
      if ((event.target as Element).closest('[role="menu"]') !== event.currentTarget) return;
      if (closeBranch && (event.key === "ArrowLeft" || event.key === "Escape")) {
        event.preventDefault();
        event.stopPropagation();
        closeBranch();
        return;
      }
      if (event.key === "ArrowRight") {
        const item = items.find(item => `${panelId}-${item.id}` === (event.target as HTMLElement).id);
        if (item?.children?.length && !item.disabled) {
          event.preventDefault();
          event.stopPropagation();
          openSubmenu(item, true);
          // Hover may have opened this branch already; move into its first row.
          const trigger = document.getElementById(`${panelId}-${item.id}`);
          const childID = trigger?.getAttribute("aria-controls");
          if (childID) document.getElementById(childID)?.querySelector<HTMLElement>('[role^="menuitem"]:not(:disabled)')?.focus();
        }
        return;
      }
      moveMenuFocus(event);
    }}
  >
    {header ? <div role="presentation" className="studio-dropdown-context">{header}</div> : null}
    {items.map((item, index) => {
      const hasChildren = Boolean(item.children?.length);
      const row = (open?: boolean, childID?: string) => <button
        id={`${panelId}-${item.id}`}
        type="button"
        role={item.checked === undefined ? "menuitem" : "menuitemradio"}
        aria-checked={item.checked}
        aria-haspopup={hasChildren ? "menu" : undefined}
        aria-expanded={hasChildren ? Boolean(open) : undefined}
        aria-controls={childID}
        data-context={!!item.description || undefined}
        data-highlighted={open || undefined}
        tabIndex={item.id === activeItemId ? 0 : -1}
        disabled={item.disabled}
        onClick={() => {
          if (hasChildren) {
            openSubmenu(item, true);
            if (childID) document.getElementById(childID)?.querySelector<HTMLElement>('[role^="menuitem"]:not(:disabled)')?.focus();
          } else {
            closeRoot();
            item.onSelect?.();
          }
        }}
        onPointerEnter={event => {
          if (event.pointerType === "touch" || item.disabled) return;
          if (hasChildren) openSubmenu(item, false);
          else setSubmenuID(null);
        }}
        onFocus={() => {
          setActiveItemId(item.id);
          if (submenuID !== item.id) setSubmenuID(null);
        }}
        className={cn(
          "studio-dropdown-option flex min-h-11 w-full items-center gap-2.5 rounded-sm px-3 text-left font-sans text-[13px] font-medium text-ink outline-none transition-colors duration-150 ease-house hover:bg-canvas-soft focus-visible:bg-canvas-soft focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--focus-ring)] disabled:pointer-events-none disabled:text-ink-faint sm:min-h-8",
          "[&_svg]:size-[16px] [&_svg]:shrink-0 [&_svg]:text-ink-soft",
          item.tone === "destructive" && "text-danger [&_svg]:text-danger",
        )}
      >
        {item.icon ? <span aria-hidden="true" className="inline-flex shrink-0">{item.icon}</span> : null}
        <span className="min-w-0 flex-1 break-words">{item.label}{item.description ? <span className="studio-dropdown-description">{item.description}</span> : null}</span>
        {item.shortcut ? <kbd className="studio-shortcut">{item.shortcut}</kbd> : null}
        {item.checked ? <CheckIcon aria-hidden="true" /> : null}
        {hasChildren ? <CaretRightIcon aria-hidden="true" /> : null}
      </button>;
      return <div key={item.id} className={(item.separated || (!!item.group && index > 0 && item.group !== items[index - 1]?.group)) ? "mt-1 border-t border-line-soft pt-1" : undefined}>
        {item.group && item.group !== items[index - 1]?.group ? <div className="studio-dropdown-heading">{item.group}</div> : null}
        {hasChildren ? <Popover
          label={item.label}
          panelRole={null}
          side="right"
          align="start"
          open={submenuID === item.id}
          focusOnOpen={focusSubmenu}
          onOpenChange={open => setSubmenuID(current => open ? item.id : current === item.id ? null : current)}
          className="w-full"
          panelClassName={menuPanelClass}
          trigger={({ open, panelId: childID }) => row(open, childID)}
        >
          {({ close, panelId: childID }) => <MenuPanel items={item.children!} label={item.label} panelId={childID} closeRoot={closeRoot} closeBranch={close} />}
        </Popover> : row()}
      </div>;
    })}
  </div>;
}

interface MenuTriggerProps {
  "aria-controls"?: string;
  "aria-expanded"?: boolean;
  "aria-haspopup"?: "menu";
}

function moveMenuFocus(event: KeyboardEvent<HTMLDivElement>): void {
  if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
  event.preventDefault();
  event.stopPropagation();
  const items = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled), [role="menuitemradio"]:not(:disabled)'));
  if (!items.length) return;
  const current = items.indexOf(document.activeElement as HTMLButtonElement);
  const next = event.key === "Home" ? items[0] : event.key === "End" ? items.at(-1) : items[(current + (event.key === "ArrowDown" ? 1 : -1) + items.length) % items.length];
  next?.focus();
}
